import { Box, Stack, styled } from '@mui/material';
import { ReadOnlyField, Show } from '@side/pantry';
import { formatAddress } from '@/common/utils/address';
import { getFullName } from '@/common/utils/person';
import { formatPhoneNumber } from '@/common/utils/phoneNumber';
import { Collapsible } from '@/components/Collapsible';
import type { AgentFields } from '@/views/Referrals/views/NewReferral/types';

interface Props {
  agent: AgentFields;
}

const Root = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    '& > .MuiStack-root': {
      flexBasis: `calc(50% - ${theme.spacing(2)})`,
    },
  },
}));

export function ReadOnlyAgentDetails({ agent }: Props) {
  const isAgentWithSide = Boolean(agent.userId);
  const brokerageAddress = formatAddress(agent.brokerage, {
    includeStreet2: true,
  });

  return (
    <Root sx={{ gap: 2 }}>
      <ReadOnlyField
        label="Name"
        value={`${agent.firstName} ${agent.lastName}`}
      />
      <ReadOnlyField label="Email" value={agent.email || null} />
      <ReadOnlyField
        label="Cell Phone #"
        value={agent.cellPhone ? formatPhoneNumber(agent.cellPhone) : null}
      />
      <Show when={isAgentWithSide}>
        <ReadOnlyField label="State" value={agent.licenseState || null} />
      </Show>
      <ReadOnlyField
        label="Agent License #"
        value={agent.licenseNumber || null}
      />
      <ReadOnlyField
        label="Brokerage Name"
        value={agent.brokerage.companyName || null}
      />
      <ReadOnlyField
        label="Managing Broker Name"
        value={
          getFullName(
            agent.supervisingBroker.firstName,
            agent.supervisingBroker.lastName,
          ) || null
        }
      />

      <Show when={!isAgentWithSide}>
        <Box sx={{ mt: 1 }}>
          <Collapsible
            label="Additional Details"
            sx={{ color: 'primary.main' }}
          >
            <Root sx={{ gap: 2 }}>
              <ReadOnlyField
                label="Managing Broker Email"
                value={agent.supervisingBroker.email || null}
              />
              <ReadOnlyField
                label="Managing Broker Cell Phone"
                value={
                  agent.supervisingBroker.cellPhone
                    ? formatPhoneNumber(agent.supervisingBroker.cellPhone)
                    : null
                }
              />
              <ReadOnlyField
                label="Brokerage License #"
                value={agent.brokerage.licenseNumber || null}
              />
              <ReadOnlyField
                label="Tax ID"
                value={agent.brokerage.taxId || null}
              />
              <ReadOnlyField label="Address" value={brokerageAddress || null} />
            </Root>
          </Collapsible>
        </Box>
      </Show>
    </Root>
  );
}
